import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useConnection, useLive, useServices, useSession, useServerConfig, ProblemError } from '@/model';
import { useNavigation } from '@/app/contract';
import type { Navigation } from '@/app/contract';
import { Banner, Button, IconButton, TextField } from '@/ui';
import { fetchMe, record } from './api';

interface Scope { accountId: string; generation: number; active: boolean; abort: AbortController; valid(ticket?: number): boolean; check(ticket?: number): void }
export function useScope() {
  const services = useServices(); const session = useSession();
  const lifetime = useLive(services.session.lifetime);
  const loginId = lifetime?.loginId; const loginGeneration = lifetime?.generation;
  const accountId = session.phase === 'signedIn' ? session.accountId : '';
  const scope = useMemo<Scope>(() => ({
    accountId, generation: 0, active: true, abort: new AbortController(),
    valid(ticket?: number) {
      const now = services.session.state.get();
      const current = services.session.lifetime.get();
      return this.active && this.generation === (ticket ?? this.generation) && now.phase === 'signedIn' && now.accountId === accountId
        && !!current && current.loginId === loginId && current.generation === loginGeneration;
    },
    check(ticket?: number) { if (!this.valid(ticket)) throw new DOMException('Cancelled', 'AbortError'); },
  }), [services, accountId, loginId, loginGeneration]);
  useEffect(() => {
    scope.active = true;
    if (scope.abort.signal.aborted) scope.abort = new AbortController();
    const changed = () => {
      if (!scope.valid()) { scope.generation++; scope.abort.abort(); }
    };
    const unsubscribe = services.session.state.subscribe(changed); const stopLifetime = services.session.lifetime.subscribe(changed);
    const stopRetire = services.session.onRetire(event => { if (event.accountId === scope.accountId) { scope.generation++; scope.abort.abort(); } });
    return () => { scope.active = false; scope.generation++; scope.abort.abort(); unsubscribe(); stopLifetime(); stopRetire(); };
  }, [services, scope]);
  return scope;
}
export function useResourcePrefix(): QueryKey {
  const session = useSession(); const config = useServerConfig();
  const context = config?.item_context;
  return ['hylo', window.location.origin, record(context) && typeof context.realm_id === 'string' ? context.realm_id : '',
    session.phase === 'signedIn' ? session.accountId : ''];
}
export function useResource<T>(name: string, load: (signal: AbortSignal) => Promise<T>,
  options: { enabled?: boolean; interval?: number | false } = {}) {
  const scope = useScope(); const prefix = useResourcePrefix(); const client = useQueryClient(); const services = useServices();
  const key = [...prefix, ...(name === 'item-preferences' ? [] : ['me']), name];
  const result = useQuery({
    queryKey: key, enabled: !!scope.accountId && (options.enabled ?? true), retry: false,
    staleTime: 0, refetchOnWindowFocus: true, refetchOnReconnect: true,
    refetchInterval: options.interval ?? false, refetchIntervalInBackground: false,
    queryFn: async ({ signal }) => {
      const ticket = scope.generation; scope.check(ticket);
      const value = await load(AbortSignal.any([signal, scope.abort.signal]));
      scope.check(ticket); return value;
    },
  });
  const keyId = JSON.stringify(key);
  const connection = useConnection(); const previousPhase = useRef(connection.phase);
  useEffect(() => {
    const was = previousPhase.current; previousPhase.current = connection.phase;
    if (was !== 'online' && connection.phase === 'online' && scope.valid()) {
      void client.invalidateQueries({ queryKey: JSON.parse(keyId) as QueryKey });
    }
  }, [connection.phase, client, keyId, scope]);
  const lostAuthority = result.error instanceof ProblemError && [401, 403, 404].includes(result.error.status);
  useEffect(() => {
    if (lostAuthority) client.removeQueries({ queryKey: JSON.parse(keyId) as QueryKey, exact: true });
  }, [lostAuthority, keyId, client]);
  useEffect(() => {
    const actualKey = JSON.parse(keyId) as QueryKey;
    return servicesUpdate();
    function servicesUpdate() {
      const stop = services.sync.onUpdate(['me.updated', 'account.updated', 'export.changed', 'export.removed', 'item.preferences.changed'],
        update => {
          if (name === 'exports' && !update.type.startsWith('export.')) return;
          if (name === 'item-preferences' && update.type !== 'item.preferences.changed') return;
          if (['devices', 'activity', 'ai'].includes(name)) return;
          if (scope.valid()) void client.invalidateQueries({ queryKey: actualKey });
        });
      const stopRetire = services.session.onRetire(event => {
        if (event.accountId !== scope.accountId) return;
        void client.cancelQueries({ queryKey: actualKey, exact: true }).catch(() => {});
        client.removeQueries({ queryKey: actualKey, exact: true });
      });
      return () => { stop(); stopRetire(); };
    }
  // service identity and a serialized stable query key define this subscription.
  }, [keyId, name, scope, client, services.sync, services.session]);
  return lostAuthority ? { ...result, data: undefined } : result;
}
export function useDetails() {
  const services = useServices();
  return useResource('details', signal => fetchMe(services, signal));
}
export function errorCopy(error: unknown, t: (key: string, options?: Record<string, unknown>) => string): string {
  if (error instanceof ProblemError) {
    if (error.code === 'consent_version_mismatch') return t('legal.changedHelp');
    if (error.code === 'invalid_credentials') return t('stepUp.wrongPassword');
    if (error.code === 'verification_code_invalid') return t('identity.wrongCode');
    if (error.code === 'verification_expired') return t('identity.expired');
    if (error.code === 'verification_unavailable') return t('identity.unavailable');
    if (error.code === 'identifier_unsupported_region') return t('identity.unsupportedRegion');
    if (error.code === 'export_in_progress') return t('export.oneAtTime');
    if (error.code === 'export_expired') return t('export.expired');
    if (error.code === 'export_not_ready') return t('export.notReady');
    if (error.code === 'assistant_unavailable') return t('ai.assistantUnavailable');
    if (error.code === 'rate_limited') return t('errors.cooldown', { count: error.retryAfter ?? 60 });
    if (['forbidden', 'claw_unavailable', 'capability_required'].includes(error.code)) return t('errors.unavailable');
  }
  return t('errors.connection');
}
export function useAction() {
  const { t } = useTranslation('me'); const scope = useScope(); const nav = useNavigation();
  const client = useQueryClient(); const prefix = useResourcePrefix(); const prefixId = JSON.stringify(prefix);
  const [busy, setBusy] = useState(false); const [error, setError] = useState<string>();
  const pending = useRef(false);
  const run = useCallback(async (action: (password?: string) => Promise<unknown>, stepUp = true, invalidate = true): Promise<boolean> => {
    if (pending.current || !scope.valid()) return false;
    pending.current = true; setBusy(true); setError(undefined);
    const ticket = scope.generation;
    try {
      if (invalidate) await client.cancelQueries({ queryKey: JSON.parse(prefixId) as QueryKey });
      scope.check(ticket);
      try { await action(); }
      catch (e) {
        if (!scope.valid(ticket)) return false;
        if (!(stepUp && e instanceof ProblemError && e.code === 'auth_reauth_required')) throw e;
        const accepted = await passwordStep(nav, scope, ticket, action);
        if (!accepted) return false;
      }
      scope.check(ticket);
      if (invalidate) await client.invalidateQueries({ queryKey: JSON.parse(prefixId) as QueryKey });
      return true;
    } catch (e) {
      if (scope.valid(ticket) && !(e instanceof DOMException && e.name === 'AbortError')) setError(errorCopy(e, t));
      return false;
    } finally { pending.current = false; if (scope.valid(ticket)) setBusy(false); }
  }, [scope, nav, client, prefixId, t]);
  return { run, busy, error, clearError: () => setError(undefined) };
}
function passwordStep(nav: Navigation, scope: ReturnType<typeof useScope>, ticket: number,
  action: (password?: string) => Promise<unknown>): Promise<boolean> {
  return new Promise(resolve => {
    let settled = false; let close = () => {};
    const abort = () => finish(false);
    const finish = (ok: boolean) => { if (settled) return; settled = true; scope.abort.signal.removeEventListener('abort', abort); resolve(ok); close(); };
    const handle = nav.present(<PasswordStep action={async password => { scope.check(ticket); await action(password); scope.check(ticket); }}
      onDone={() => finish(true)} onCancel={() => finish(false)} />, { size: 'small', onDismiss: () => finish(false) });
    close = handle.dismiss;
    scope.abort.signal.addEventListener('abort', abort, { once: true });
  });
}
function PasswordStep({ action, onDone, onCancel }: {
  action: (password: string) => Promise<void>; onDone: () => void; onCancel: () => void;
}) {
  const { t } = useTranslation('me'); const [password, setPassword] = useState(''); const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false); const [error, setError] = useState<string>(); const pending = useRef(false);
  return <form className="me-sheet" onSubmit={event => {
    event.preventDefault(); if (!password || pending.current) return;
    pending.current = true; setBusy(true); setError(undefined);
    void action(password).then(() => { setPassword(''); onDone(); }).catch(e => setError(errorCopy(e, t)))
      .finally(() => { pending.current = false; setBusy(false); });
  }}>
    <h2>{t('stepUp.title')}</h2><p className="me-footnote">{t('stepUp.help')}</p>
    <TextField autoFocus label={t('stepUp.password')} type={show ? 'text' : 'password'} value={password} autoComplete="current-password"
      maxLength={1024} onChange={e => setPassword(e.target.value)} trailing={<IconButton name={show ? 'eye.slash' : 'eye'} label={t(show ? 'a11y.hidePassword' : 'a11y.showPassword')} onClick={() => setShow(!show)} />} />
    {error && <Banner tone="error">{error}</Banner>}
    <div className="me-actions"><Button variant="plain" onClick={() => { setPassword(''); onCancel(); }} disabled={busy}>{t('common:cancel')}</Button>
      <Button type="submit" loading={busy} disabled={!password} variant="prominent">{t('actions.continue')}</Button></div>
  </form>;
}

